import { deepCopy } from '../utils/object.utils';

export enum FeaturePermission {
  Feature_KnowledgeBase = 'feature_knowledge-base',
  Permission_KnowledgeBase_Edit = 'permission_knowledge-base_edit',
  Permission_KnowledgeBase_View = 'permission_knowledge-base_view',

  Feature_Sites = 'feature_sites',
  Permission_Sites_Edit = 'permission_sites_edit',
  Permission_Sites_View = 'permission_sites_view',

  Feature_Elements = 'feature_elements',
  Permission_Elements_Edit = 'permission_elements_edit',
  Permission_Elements_View = 'permission_elements_view',

  Feature_Tracking = 'feature_tracking',
  Permission_Tracking_View = 'permission_tracking_view',

  Feature_Hints = 'feature_hints',
  Permission_Hints_Edit = 'permission_hints_edit',
  Permission_Hints_View = 'permission_hints_view',

  Feature_Forms = 'feature_forms',
  Feature_FormsNotifications = 'feature_forms-notifications',

  Feature_Checklists = 'feature_checklists',
  Permission_Checklists_Edit = 'permission_checklists_edit',
  Permission_Checklists_View = 'permission_checklists_view',

  Permission_Forms_Edit = 'permission_forms_edit',
  Permission_Forms_View = 'permission_forms_view',

  Feature_VisitorExplorer = 'feature_visitor-explorer',
  Permission_VisitorExplorer_View = 'permission_visitor-explorer_view',

  Permission_Files_Edit = 'permission_files_edit',
  Permission_Files_View = 'permission_files_view',

  Permission_Subscriptions_Admin = 'permission_subscriptions_admin',

  Permission_Members_Edit = 'permission_members_edit',
  Permission_Members_View = 'permission_members_view',
}

export enum FeatureLimitationKey {
  MonthlyActiveUsers = 'monthlyActiveUsers',
  MaxKnowledgeBaseCount = 'maxKnowledgeBaseCount',
  MaxArticleCount = 'maxArticleCount',
  MaxKnowledgeBaseDomainCount = 'maxKnowledgeBaseDomainCount',
}

export interface FeatureLimitations {
  [FeatureLimitationKey.MonthlyActiveUsers]: number;
  [FeatureLimitationKey.MaxKnowledgeBaseCount]: number;
  [FeatureLimitationKey.MaxArticleCount]: number;
  [FeatureLimitationKey.MaxKnowledgeBaseDomainCount]: number;
}

export const DEFAULT_FEATURE_LIMITATIONS: FeatureLimitations = {
  [FeatureLimitationKey.MonthlyActiveUsers]: 0,
  [FeatureLimitationKey.MaxKnowledgeBaseCount]: 0,
  [FeatureLimitationKey.MaxArticleCount]: 0,
  [FeatureLimitationKey.MaxKnowledgeBaseDomainCount]: 0,
};

export const DEFAULT_FEATURE_PERMISSIONS: FeaturePermissions = {
  permissions: [],
  limitations: DEFAULT_FEATURE_LIMITATIONS,
};

type FeatureLimitationMergeStrategy = 'greater' | 'lesser';

const FeatureLimitationMergeStrategyMappings: {
  [limitation in FeatureLimitationKey]: FeatureLimitationMergeStrategy;
} = {
  [FeatureLimitationKey.MonthlyActiveUsers]: 'greater',
  [FeatureLimitationKey.MaxKnowledgeBaseCount]: 'greater',
  [FeatureLimitationKey.MaxArticleCount]: 'greater',
  [FeatureLimitationKey.MaxKnowledgeBaseDomainCount]: 'greater',
};

export interface FeaturePermissions {
  permissions: FeaturePermission[];

  limitations: FeatureLimitations;
}

export function isFeatureEnabled(key: FeaturePermission, permissions: FeaturePermissions | undefined): boolean {
  if (!permissions?.permissions?.length) {
    return false;
  }

  return permissions.permissions.includes(key);
}

export function isFeatureDisabled(key: FeaturePermission, permissions: FeaturePermissions | undefined): boolean {
  return !isFeatureEnabled(key, permissions);
}

export function isAnyFeatureEnabled(
  permissionKeys: FeaturePermission[],
  permissions: FeaturePermissions | undefined,
): boolean {
  return permissionKeys.some((_) => isFeatureEnabled(_, permissions));
}

export function areFeaturesEnabled(
  permissionKeys: FeaturePermission[],
  permissions: FeaturePermissions | undefined,
): boolean {
  return !permissionKeys.some((_) => !isFeatureEnabled(_, permissions));
}

export function mergeFeatureLimitations(base: FeatureLimitations, limitations: Partial<FeatureLimitations>[]): Readonly<FeatureLimitations> {
  const result = <FeatureLimitations>deepCopy(base);

  for (const limitation of limitations) {
    for (const [prop, strategy] of <[FeatureLimitationKey, FeatureLimitationMergeStrategy][]>Object.entries(FeatureLimitationMergeStrategyMappings)) {
      const limitationValue = limitation[prop];

      if (limitationValue === undefined) {
        continue;
      }

      if (
        (strategy === 'greater' && limitationValue > result[prop])
        ||
        (strategy === 'lesser' && limitationValue < result[prop])
      ) {
        result[prop] = <any>limitationValue;
      }
    }
  }

  return result;
}
